import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, Tag } from 'antd';
import type { ColumnsType, ExpandableConfig } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { useP503, ItemState } from './hooks/useP503';
import type { TestItemInfo } from '../../services/p503Api';
import { p503Api } from '../../services/p503Api';
import { BatchAcceptanceBridge, pwp503f1AcceptanceManifest } from '../../features/acceptance';
import './P503Form.css';

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '';
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

const OUTPUT_FORMAT_LABELS: Record<number, string> = {
  1: 'CHANGE/COT/NTL/EOT',
  2: 'DRY/WET',
  3: 'WARP/WEFT',
  4: 'BEFORE WASH/3WASH',
  5: '單一結果',
};

const OUTPUT_FORMAT_COLORS: Record<number, string> = {
  1: 'blue',
  2: 'cyan',
  3: 'geekblue',
  4: 'purple',
  5: 'default',
};

const P503Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');

  const {
    items,
    loading,
    executeLoading,
    queried,
    lastApiResponse,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    handleExecute,
  } = useP503();

  const onQuery = useCallback(() => {
    handleQuery(startNo, endNo);
  }, [handleQuery, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  const onExecute = useCallback(() => {
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准已勾選的 ${selectedCount} 筆測試報告？`,
      okText: '確定核准',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  const allSelected =
    items.length > 0 && items.every((s) => s.selected);
  const candidateRecords = items.map((state) => state.item);
  const selectedKeys = items.filter((state) => state.selected).map((state) => state.item.fp230_01);
  const queryAcceptanceExact = useCallback((id: string) => p503Api.query(id, id), []);

  const expandable: ExpandableConfig<ItemState> = {
    expandedRowRender: (record: ItemState) => {
      const testItems: TestItemInfo[] = record.item.test_items ?? [];
      if (testItems.length === 0) {
        return <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>無測試項目</span>;
      }
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '4px 0' }}>
          {testItems.map((ti, idx) => (
            <Tag key={`${ti.code}-${idx}`} color={OUTPUT_FORMAT_COLORS[ti.output_format] ?? 'default'}>
              {ti.code}
              {ti.name ? ` ${ti.name}` : ''}
              {ti.output_format > 0 && (
                <span style={{ marginLeft: 4, opacity: 0.8 }}>
                  [{OUTPUT_FORMAT_LABELS[ti.output_format] ?? `格式${ti.output_format}`}]
                </span>
              )}
            </Tag>
          ))}
        </div>
      );
    },
    rowExpandable: (record: ItemState) =>
      (record.item.test_items?.length ?? 0) > 0,
  };

  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected && items.length > 0}
          onChange={(e) => handleSelectAll(e.target.checked)}
          disabled={items.length === 0}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) =>
            handleToggleItem(record.item.fp230_01, e.target.checked)
          }
        />
      ),
    },
    {
      title: '報告單號',
      dataIndex: ['item', 'fp230_01'],
      key: 'fp230_01',
      width: 140,
    },
    {
      title: '報告日期',
      dataIndex: ['item', 'fp230_11'],
      key: 'fp230_11',
      width: 110,
      render: (v: string | null) => fmtDate(v),
    },
    {
      title: '送件單號',
      dataIndex: ['item', 'fp230_13'],
      key: 'fp230_13',
      width: 120,
    },
    {
      title: '缸號',
      dataIndex: ['item', 'fp230_14'],
      key: 'fp230_14',
      width: 80,
    },
    {
      title: '色名',
      dataIndex: ['item', 'fp230_16'],
      key: 'fp230_16',
      width: 120,
    },
    {
      title: '廠商',
      key: 'supplier',
      width: 160,
      render: (_: unknown, record: ItemState) => {
        const code = record.item.fp230_18;
        const name = record.item.supplier_name;
        if (code && name) return `${code} - ${name}`;
        return code || name || '';
      },
    },
    {
      title: '狀態',
      key: 'status',
      width: 80,
      align: 'center',
      render: (_: unknown, record: ItemState) => {
        const s = record.item.fp230_80;
        if (s === 'P') return <Tag color="success">已核准</Tag>;
        if (s === 'A') return <Tag color="processing">待核准</Tag>;
        return <Tag>{s || 'N/A'}</Tag>;
      },
    },
  ];

  return (
    <div className="p503-form">
      {(loading || executeLoading) && (
        <div className="loading-overlay">
          {executeLoading ? '核准執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                品保管理
              </a>
              <span>/</span>
              <span className="current">物化性測試報告核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>物化性測試報告核准</h1>
            <span className="badge">P503</span>
          </div>
        </div>

        <div className="query-bar">
          <label data-contract-field="fp230_01">報告單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 180 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 180 }}
            allowClear
          />
          <Button data-acceptance-action="PWP503-ACT-QUERY" type="primary" onClick={onQuery} loading={loading}>
            確定範圍
          </Button>
        </div>

        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fp230_01}
              expandable={expandable}
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入報告單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        <div className="action-bar">
          <Button
            data-acceptance-action="PWP503-ACT-APPROVE"
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executeLoading}
            loading={executeLoading}
            onClick={onExecute}
          >
            核准 ({selectedCount})
          </Button>
          <Button danger size="large" onClick={() => navigate('/erp')}>
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwp503f1AcceptanceManifest}
          records={candidateRecords}
          selectedKeys={selectedKeys}
          keyOf={(item) => item.fp230_01}
          queryExact={queryAcceptanceExact}
          lastApiResponse={lastApiResponse}
          loading={loading || executeLoading}
          onAutoFill={() => {
            if (candidateRecords[0]) handleToggleItem(candidateRecords[0].fp230_01, true);
          }}
          autoFillDisabled={candidateRecords.length === 0}
          autoFillReason="先查詢 A 狀態候選報告，再安全勾選第一筆；不會自動核准。"
          screenDraft={{ startNo, endNo, selected_items: selectedKeys, candidates: candidateRecords }}
        />
      </div>
    </div>
  );
};

export default P503Form;
